import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import RCAUserWithAvatar from "pages/Corporate/RCA/components/RCAUserWithAvatar";
import { IRCA } from "entities/RCA/RCALibraryEntity/RCALibraryEntityInterfaces";
import RCALibraryDescriptionCell from "./components/RCALibraryDescriptionCell";
import RCALibraryDescriptionExpandedCell from "./components/RCALibraryDescriptionExpandedCell";
import RCALibraryDueDateCell from "./components/RCALibraryDueDateCell";
import RCALibraryStatusCell from "./components/RCALibraryStatusCell";
import { IActionPlan } from "entities/RCA/ActionPlanEntity/ActionPlanEntityInterfaces";
import RCALibraryNumberExpandedCell from "./components/RCALibraryNumberExpandedCell";
import RCALibraryNumberCell from "./components/RCALibraryNumberCell";
import useDateFormat from "hooks/useDateFormat";
import RCALibraryOptionsMenuCell from "./components/RCALibraryOptionsMenuCell/RCALibraryOptionsMenuCell";
import { ApolloQueryResult, OperationVariables } from "@apollo/client";

interface IRCALibraryColumns extends Omit<IMainTableColumn, "render" | "renderExpandedRow"> {
    render: (row: IRCA) => string | JSX.Element | number;
    renderExpandedRow?: (row: IActionPlan) => string | JSX.Element | number;
}

const useRCALibraryColumns = (
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>,
): IRCALibraryColumns[] => {
    const { t } = useTranslation();
    const { formatDate } = useDateFormat();

    const columns = useMemo(
        (): IRCALibraryColumns[] => [
            {
                id: "number",
                label: t("rca.rca_library.number"),
                hideSortIcon: false,
                customStyles: { width: "10%" },
                render: (row) => <RCALibraryNumberCell rca={row} />,
                renderExpandedRow: (row) => <RCALibraryNumberExpandedCell actionPlan={row} />,
            },
            {
                id: "description",
                label: t("rca.rca_library.description"),
                hideSortIcon: false,
                customStyles: { width: "30%" },
                render: (row) => <RCALibraryDescriptionCell rca={row} />,
                renderExpandedRow: (row) => <RCALibraryDescriptionExpandedCell actionPlan={row} />,
            },
            {
                id: "due_date",
                label: t("rca.rca_library.due_date"),
                hideSortIcon: false,
                customStyles: { width: "15%" },
                align: "center",
                render: (row) => <RCALibraryDueDateCell rca={row} />,
            },
            {
                id: "status",
                label: t("rca.rca_library.status"),
                hideSortIcon: false,
                customStyles: { width: "15%" },
                render: (row) => <RCALibraryStatusCell rca={row} />,
            },
            {
                id: "completed",
                label: t("rca.rca_library.completed"),
                hideSortIcon: false,
                customStyles: { width: "10%" },
                render: (row) => (row.date_held ? formatDate(row.date_held) : "--"),
            },
            {
                id: "owner",
                label: t("rca.rca_library.owner"),
                hideSortIcon: false,
                customStyles: { width: "15%" },
                render: (row) => (
                    <RCAUserWithAvatar
                        imageUrl={row.owner?.media?.media_url || ""}
                        name={`${row.owner?.first_name} ${row.owner?.last_name}` || ""}
                    />
                ),
                renderExpandedRow: (row) => {
                    return (
                        <RCAUserWithAvatar
                            imageUrl={row.submitted_by?.media?.media_url || ""}
                            name={`${row.submitted_by?.first_name} ${row.submitted_by?.last_name}` || ""}
                        />
                    );
                },
            },
            {
                id: "options",
                label: "",
                hideSortIcon: true,
                customStyles: { width: "5%" },
                render: (row) => <RCALibraryOptionsMenuCell refetch={refetch} rca={row} />,
                renderExpandedRow: (row) => "",
            },
        ],
        [t, formatDate, refetch],
    );

    return columns;
};

export default useRCALibraryColumns;
